import { useState } from 'react';
import {
  Button, Chip, Stack, TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Alert, Snackbar, IconButton, Tooltip, Table, TableHead, TableRow, TableCell, TableBody, Divider,
} from '@mui/material';
import { EpcProjectSelect } from '../../components/EpcProjectSelect';
import { Add, Delete, ReceiptLong, UploadFile } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { FileUploadField } from '../../components/FileUploadField';
import { epcBoqApi, type EpcBoq, type BoqItemType, type ParsedBoqRow } from '../../api/epcBoq.api';
import { epcApi } from '../../api/epc.api';
import { clientsApi } from '../../api/clients.api';
import { projectsApi } from '../../api/projects.api';
import { sitesApi } from '../../api/sites.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;
const ITEM_TYPES: BoqItemType[] = ['material', 'labour', 'equipment', 'other'];
const statusColor = (s: string): 'default' | 'info' | 'success' | 'error' | 'warning' =>
  s === 'approved' ? 'success' : s === 'submitted' ? 'info' : s === 'rejected' ? 'error' : s === 'revised' ? 'warning' : 'default';

export function EpcBoqPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [projectId, setProjectId] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const pid = projectId ? Number(projectId) : undefined;
  const { data, isLoading } = useQuery({
    queryKey: ['epc-boqs', page, limit, pid],
    queryFn: () => epcBoqApi.list({ page, limit, projectId: pid }),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['epc-boqs'] });

  const columns: Column<EpcBoq>[] = [
    { key: 'boqCode', label: 'BOQ Code', render: (r) => <span>{r.boqCode}<br /><small style={{ color: '#888' }}>v{r.version}{r.parentBoqId ? ' (revision)' : ''}</small></span> },
    { key: 'clientName', label: 'Client', render: (r) => r.clientName ?? '—' },
    { key: 'projectName', label: 'Project', render: (r) => r.projectName ? `${r.projectName}${r.projectCode ? ` (${r.projectCode})` : ''}` : `#${r.projectId}` },
    { key: 'stateName', label: 'State', render: (r) => r.stateName ?? '—' },
    { key: 'regionName', label: 'Region', render: (r) => r.regionName ?? '—' },
    { key: 'projectManagerName', label: 'Project Mgr', render: (r) => r.projectManagerName ?? '—' },
    { key: 'romName', label: 'ROM', render: (r) => r.romName ?? '—' },
    { key: 'title', label: 'Title', render: (r) => r.title ?? '—' },
    { key: 'totalAmount', label: 'Total', align: 'right', render: (r) => inr(r.totalAmount) },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" label={r.status} color={statusColor(r.status)} /> },
    { key: 'createdAt', label: 'Created', render: (r) => new Date(r.createdAt).toLocaleDateString('en-IN') },
  ];

  return (
    <>
      <PageHeader title="EPC Bill of Quantities" subtitle="Versioned scope definition — line items, approval, and revisions."
        action={hasPermission('epc_boq.create') && (
          <Stack direction="row" spacing={1}>
            <Button startIcon={<UploadFile />} variant="outlined" onClick={() => { setError(null); setUploadOpen(true); }}>Upload BOQ</Button>
            <Button startIcon={<Add />} variant="contained" disabled={!pid} onClick={() => { setError(null); setCreateOpen(true); }}>New BOQ</Button>
          </Stack>
        )} />

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <EpcProjectSelect value={projectId} onChange={(v) => { setProjectId(v); setPage(1); }} helperText={pid ? undefined : 'Select a project'} />
      </Stack>

      <DataTable
        columns={columns} rows={data?.data ?? []} loading={isLoading}
        page={page} limit={limit} total={data?.meta.total ?? 0}
        onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} rowKey={(r) => r.id}
        actions={(r) => (
          <Tooltip title="Open"><IconButton size="small" onClick={() => setDetailId(r.id)}><ReceiptLong fontSize="small" /></IconButton></Tooltip>
        )}
      />

      <CreateBoqDialog open={createOpen} projectId={pid} onClose={() => setCreateOpen(false)}
        onDone={(id) => { setCreateOpen(false); invalidate(); setDetailId(id); }} onError={setError} error={error} />

      {uploadOpen && <UploadBoqDialog onClose={() => setUploadOpen(false)}
        onDone={(id, msg) => { setUploadOpen(false); invalidate(); setToast(msg); setDetailId(id); }} />}

      {detailId && <BoqDetailDialog id={detailId} onClose={() => setDetailId(null)} onChanged={invalidate} onToast={setToast} />}

      <Snackbar open={Boolean(toast)} autoHideDuration={5000} onClose={() => setToast(null)}>
        <Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </>
  );
}

function CreateBoqDialog({ open, projectId, onClose, onDone, onError, error }: {
  open: boolean; projectId?: number; onClose: () => void; onDone: (id: number) => void; onError: (e: string) => void; error: string | null;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const mut = useMutation({
    mutationFn: () => epcBoqApi.create({ projectId: projectId!, title: title || undefined, description: description || undefined }),
    onSuccess: (b) => { setTitle(''); setDescription(''); onDone(b.id); },
    onError: (e) => onError(extractApiError(e)),
  });
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New BOQ — Project #{projectId}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Title" size="small" value={title} onChange={(e) => setTitle(e.target.value)} />
          <TextField label="Description" size="small" multiline minRows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={mut.isPending} onClick={() => mut.mutate()}>Create</Button>
      </DialogActions>
    </Dialog>
  );
}

function UploadBoqDialog({ onClose, onDone }: { onClose: () => void; onDone: (id: number, msg: string) => void }) {
  const [clientId, setClientId] = useState<number | ''>('');
  const [projectId, setProjectId] = useState<number | ''>('');
  const [siteId, setSiteId] = useState<number | ''>('');
  const [title, setTitle] = useState('');
  const [fileUrl, setFileUrl] = useState('');
  const [rows, setRows] = useState<ParsedBoqRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const clients = useQuery({ queryKey: ['clients', 'active'], queryFn: () => clientsApi.list({ limit: 500, status: 'active' }) });
  const projects = useQuery({ queryKey: ['projects', 'all'], queryFn: () => projectsApi.list({ limit: 1000 }) });
  const sites = useQuery({ queryKey: ['sites', 'all'], queryFn: () => sitesApi.list({ limit: 1000 }) });
  const clientProjects = (projects.data?.data ?? []).filter((p) => p.clientId === clientId);
  const projectSites = (sites.data?.data ?? []).filter((s) => s.projectId === projectId);

  const parseMut = useMutation({
    mutationFn: () => epcBoqApi.parse(fileUrl),
    onSuccess: (r) => {
      setError(null);
      if (!r.rows.length) { setInfo(r.message ?? 'No line items detected — check the file.'); setRows([]); return; }
      setRows(r.rows);
      setInfo(`Parsed ${r.rows.length} line item(s) — review below, then save.`);
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const saveMut = useMutation({
    mutationFn: () => epcBoqApi.uploadForSite({ siteId: Number(siteId), title: title || undefined, rows }),
    onSuccess: (b) => onDone(b.id, `BOQ ${b.boqCode} created & mapped to the site (${rows.length} items)`),
    onError: (e) => setError(extractApiError(e)),
  });

  const setRow = (i: number, k: keyof ParsedBoqRow, v: unknown) => setRows((arr) => arr.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const total = rows.reduce((s, r) => s + (Number(r.quantity) || 0) * (Number(r.clientRate ?? r.executionRate) || 0), 0);
  const canSave = Boolean(siteId) && rows.length > 0;

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Upload BOQ — map to a site</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <TextField select size="small" label="Client *" value={clientId} sx={{ minWidth: 220 }}
              onChange={(e) => { setClientId(e.target.value ? Number(e.target.value) : ''); setProjectId(''); setSiteId(''); }}>
              {(clients.data?.data ?? []).map((c) => <MenuItem key={c.id} value={c.id}>{c.clientName}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Project *" value={projectId} disabled={!clientId} sx={{ minWidth: 240 }}
              onChange={(e) => { setProjectId(e.target.value ? Number(e.target.value) : ''); setSiteId(''); }}>
              {clientProjects.map((p) => <MenuItem key={p.id} value={p.id}>{p.projectCode} — {p.projectName}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Site *" value={siteId} disabled={!projectId} sx={{ minWidth: 220 }}
              onChange={(e) => setSiteId(e.target.value ? Number(e.target.value) : '')}>
              {projectSites.map((s) => <MenuItem key={s.id} value={s.id}>{s.siteName} ({s.siteCode})</MenuItem>)}
            </TextField>
          </Stack>
          <TextField size="small" label="BOQ Title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} sx={{ maxWidth: 400 }} />

          <Divider textAlign="left"><Typography variant="caption" color="text.secondary">Upload BOQ file (Excel / CSV)</Typography></Divider>
          <Alert severity="info" sx={{ py: 0 }}>
            Use the standard template so columns map correctly. <b>Item Description</b> = the actual line item; <b>BOQ Category</b> = its section/group.
            &nbsp;<Button size="small" onClick={() => epcBoqApi.downloadTemplate().catch(() => setError('Could not download the template'))}>Download template</Button>
          </Alert>
          <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap" useFlexGap>
            <FileUploadField label="BOQ file" accept=".xlsx,.xls,.csv" value={fileUrl} onChange={(url) => { setFileUrl(url ?? ''); setRows([]); setInfo(null); }} />
            <Button variant="outlined" disabled={!fileUrl || parseMut.isPending} onClick={() => { setInfo(null); parseMut.mutate(); }}>Parse file</Button>
          </Stack>
          {info && <Alert severity="info" sx={{ py: 0 }}>{info}</Alert>}

          {rows.length > 0 && (
            <>
              <Divider textAlign="left"><Typography variant="caption" color="text.secondary">Line items (review & edit)</Typography></Divider>
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell>Description *</TableCell><TableCell>Category</TableCell><TableCell>SOW</TableCell>
                  <TableCell>Unit</TableCell><TableCell align="right">Qty</TableCell><TableCell align="right">Rate</TableCell><TableCell align="right">Amount</TableCell><TableCell />
                </TableRow></TableHead>
                <TableBody>
                  {rows.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell><TextField size="small" variant="standard" fullWidth value={r.description} onChange={(e) => setRow(i, 'description', e.target.value)} /></TableCell>
                      <TableCell><TextField size="small" variant="standard" value={r.category ?? ''} onChange={(e) => setRow(i, 'category', e.target.value)} sx={{ width: 110 }} /></TableCell>
                      <TableCell><TextField size="small" variant="standard" value={r.sow ?? ''} onChange={(e) => setRow(i, 'sow', e.target.value)} sx={{ width: 110 }} /></TableCell>
                      <TableCell><TextField size="small" variant="standard" value={r.unit ?? ''} onChange={(e) => setRow(i, 'unit', e.target.value)} sx={{ width: 60 }} /></TableCell>
                      <TableCell align="right"><TextField size="small" variant="standard" type="number" value={r.quantity ?? ''} onChange={(e) => setRow(i, 'quantity', e.target.value === '' ? null : Number(e.target.value))} sx={{ width: 70 }} /></TableCell>
                      <TableCell align="right"><TextField size="small" variant="standard" type="number" value={r.clientRate ?? ''} onChange={(e) => setRow(i, 'clientRate', e.target.value === '' ? null : Number(e.target.value))} sx={{ width: 90 }} /></TableCell>
                      <TableCell align="right">{inr((Number(r.quantity) || 0) * (Number(r.clientRate ?? r.executionRate) || 0))}</TableCell>
                      <TableCell><IconButton size="small" color="error" onClick={() => setRows((arr) => arr.filter((_, j) => j !== i))}><Delete fontSize="small" /></IconButton></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Typography variant="subtitle1" align="right">Total: {inr(total)}</Typography>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canSave || saveMut.isPending} onClick={() => saveMut.mutate()}>Save BOQ</Button>
      </DialogActions>
    </Dialog>
  );
}

function BoqDetailDialog({ id, onClose, onChanged, onToast }: {
  id: number; onClose: () => void; onChanged: () => void; onToast: (m: string) => void;
}) {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [err, setErr] = useState<string | null>(null);
  const { data: boq } = useQuery({ queryKey: ['epc-boq', id], queryFn: () => epcBoqApi.get(id) });
  const { data: milestones } = useQuery({
    queryKey: ['epc-milestones', 'for-boq', boq?.projectId],
    queryFn: () => epcApi.listMilestones({ projectId: boq!.projectId, limit: 200 }),
    enabled: Boolean(boq?.projectId),
  });

  const refresh = () => { qc.invalidateQueries({ queryKey: ['epc-boq', id] }); onChanged(); };
  const act = (fn: () => Promise<unknown>, msg: string) =>
    fn().then(() => { refresh(); onToast(msg); }).catch((e) => setErr(extractApiError(e)));

  // new item form
  const [it, setIt] = useState({ itemType: 'material' as BoqItemType, description: '', unit: '', quantity: '', rate: '', milestoneId: '' });
  const addItem = useMutation({
    mutationFn: () => epcBoqApi.addItem(id, {
      itemType: it.itemType, description: it.description, unit: it.unit || undefined,
      quantity: Number(it.quantity), rate: Number(it.rate), milestoneId: it.milestoneId ? Number(it.milestoneId) : undefined,
    }),
    onSuccess: () => { setIt({ itemType: 'material', description: '', unit: '', quantity: '', rate: '', milestoneId: '' }); refresh(); },
    onError: (e) => setErr(extractApiError(e)),
  });

  if (!boq) return null;
  const isDraft = boq.status === 'draft';
  const canEdit = isDraft && hasPermission('epc_boq.update');

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {boq.boqCode} <Chip size="small" sx={{ ml: 1 }} label={boq.status} color={statusColor(boq.status)} />
        <Typography variant="body2" color="text.secondary">Project #{boq.projectId} · v{boq.version} · Total {inr(boq.totalAmount)}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr(null)}>{err}</Alert>}
        {boq.title && <Typography variant="subtitle2">{boq.title}</Typography>}
        {boq.description && <Typography variant="body2" color="text.secondary" gutterBottom>{boq.description}</Typography>}
        {boq.status === 'rejected' && boq.remarks && <Alert severity="warning" sx={{ my: 1 }}>Rejected: {boq.remarks}</Alert>}

        <Table size="small" sx={{ mt: 1 }}>
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell><TableCell>Description</TableCell><TableCell>Milestone</TableCell>
              <TableCell align="right">Qty</TableCell><TableCell>Unit</TableCell>
              <TableCell align="right">Rate</TableCell><TableCell align="right">Amount</TableCell>
              {canEdit && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {(boq.items ?? []).map((row) => (
              <TableRow key={row.id}>
                <TableCell><Chip size="small" variant="outlined" label={row.itemType} /></TableCell>
                <TableCell>{row.description}</TableCell>
                <TableCell>{row.milestoneId ? `#${row.milestoneId}` : '—'}</TableCell>
                <TableCell align="right">{row.quantity}</TableCell>
                <TableCell>{row.unit ?? '—'}</TableCell>
                <TableCell align="right">{inr(row.rate)}</TableCell>
                <TableCell align="right">{inr(row.amount)}</TableCell>
                {canEdit && <TableCell><IconButton size="small" color="error" onClick={() => act(() => epcBoqApi.removeItem(id, row.id), 'Item removed')}><Delete fontSize="small" /></IconButton></TableCell>}
              </TableRow>
            ))}
            {(boq.items ?? []).length === 0 && <TableRow><TableCell colSpan={canEdit ? 8 : 7}><Typography variant="body2" color="text.secondary">No line items yet.</Typography></TableCell></TableRow>}
          </TableBody>
        </Table>

        {canEdit && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>Add line item</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              <TextField select size="small" label="Type" value={it.itemType} onChange={(e) => setIt({ ...it, itemType: e.target.value as BoqItemType })} sx={{ width: 120 }}>
                {ITEM_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
              <TextField size="small" label="Description" value={it.description} onChange={(e) => setIt({ ...it, description: e.target.value })} sx={{ minWidth: 180 }} />
              <TextField select size="small" label="Milestone" value={it.milestoneId} onChange={(e) => setIt({ ...it, milestoneId: e.target.value })} sx={{ width: 160 }}>
                <MenuItem value="">— none —</MenuItem>
                {(milestones?.data ?? []).map((m) => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
              </TextField>
              <TextField size="small" label="Qty" type="number" value={it.quantity} onChange={(e) => setIt({ ...it, quantity: e.target.value })} sx={{ width: 90 }} />
              <TextField size="small" label="Unit" value={it.unit} onChange={(e) => setIt({ ...it, unit: e.target.value })} sx={{ width: 90 }} />
              <TextField size="small" label="Rate" type="number" value={it.rate} onChange={(e) => setIt({ ...it, rate: e.target.value })} sx={{ width: 110 }} />
              <Button startIcon={<Add />} variant="outlined" disabled={!it.description || !it.quantity || !it.rate || addItem.isPending} onClick={() => addItem.mutate()}>Add</Button>
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {isDraft && hasPermission('epc_boq.delete') && <Button color="error" onClick={() => act(() => epcBoqApi.remove(id).then(() => onClose()), 'BOQ deleted')}>Delete</Button>}
        {isDraft && hasPermission('epc_boq.submit') && <Button variant="contained" onClick={() => act(() => epcBoqApi.submit(id), 'BOQ submitted for approval')}>Submit</Button>}
        {boq.status === 'submitted' && hasPermission('epc_boq.approve') && (
          <>
            <Button color="error" onClick={() => { const r = window.prompt('Rejection reason?'); if (r) act(() => epcBoqApi.reject(id, r), 'BOQ rejected'); }}>Reject</Button>
            <Button variant="contained" color="success" onClick={() => act(() => epcBoqApi.approve(id), 'BOQ approved')}>Approve</Button>
          </>
        )}
        {(boq.status === 'approved' || boq.status === 'rejected') && hasPermission('epc_boq.create') && (
          <Button variant="contained" onClick={() => act(() => epcBoqApi.revise(id).then(() => onClose()), 'New revision created (draft)')}>Revise</Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
